import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';

export type ShareMethod = 'whatsapp' | 'email';

export interface IShareRecipient {
  method: ShareMethod;
  name: string;
  contact: string;
}

// Opened by the Share / Share All buttons to pick how a share goes out and who it's for. For email
// the contact is the mailto "to" address; for WhatsApp there's no equivalent since the share hands
// the PDF off to the OS share sheet (see handleRecipientShare in FloorplanModal/GridHeader) rather
// than a wa.me link, so the name/number are for the Units Shared activity log only - they don't
// pre-select a WhatsApp chat.
const ShareRecipientDialog = (props: { open: boolean; suites: string[]; onClose: () => void; onSubmit: (recipient: IShareRecipient) => void }) => {
  const { open, suites, onClose, onSubmit } = props;
  const [method, setMethod] = useState<ShareMethod>('whatsapp');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');

  useEffect(() => {
    if (open) {
      setMethod('whatsapp');
      setName('');
      setContact('');
    }
  }, [open]);

  const isEmail = method === 'email';
  // WhatsApp's phone number is just a record for the activity log, so it's optional. Email still
  // needs an address since it's actually used as the mailto "to".
  const contactValid = isEmail ? contact.includes('@') : contact.trim() === '' || contact.replace(/\D/g, '').length >= 7;
  const canSubmit = name.trim().length > 0 && contactValid;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ method, name: name.trim(), contact: contact.trim() });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle><strong>Share</strong></DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3, minWidth: 320 }}>
        <Typography variant="body2">
          {suites.length > 5 ? `${suites.length} units selected` : `Units: ${suites.join(', ')}`}
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          size="small"
          value={method}
          sx={{
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { backgroundColor: 'primary.dark' },
            },
          }}
          onChange={(_e, value: ShareMethod | null) => {
            if (value) {
              setMethod(value);
              setContact('');
            }
          }}
        >
          <ToggleButton value="whatsapp">
            <WhatsAppIcon fontSize="small" sx={{ mr: 1 }} />
            WhatsApp
          </ToggleButton>
          <ToggleButton value="email">
            <EmailIcon fontSize="small" sx={{ mr: 1 }} />
            Email
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField autoFocus label="Recipient Full Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
        <TextField
          label={isEmail ? 'Recipient Email' : 'Recipient Phone Number (optional)'}
          type={isEmail ? 'email' : 'tel'}
          placeholder={isEmail ? 'name@example.com' : 'e.g. 14165551234'}
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={!canSubmit} onClick={handleSubmit}>
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareRecipientDialog;
